import React, { useState } from "react";
import ScrollReveal from "../Common/ScrollReveal";

const THEME_PRIMARY = "#18305b";
const SLIDER_TRACK = "#e2e8f0";

const LoanCalculator = () => {
  const sliderStyle = {
    WebkitAppearance: "none",
    appearance: "none",
    background: "transparent",
    cursor: "pointer",
  };
  const [loanAmount, setLoanAmount] = useState(10000);
  const [loanTerm, setLoanTerm] = useState(48);
  const [interestRate, setInterestRate] = useState(0.75);

  const calculateEMI = () => {
    const principal = loanAmount;
    const rate = interestRate / 100;
    const months = loanTerm;
    if (rate === 0) return principal / months;
    return (principal * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
  };

  const monthlyEMI = calculateEMI();
  const totalPayable = monthlyEMI * loanTerm;
  const apr = ((totalPayable - loanAmount) / loanAmount) * 100;

  const sliderGradient = (pct) =>
    `linear-gradient(to right, ${THEME_PRIMARY} 0%, ${THEME_PRIMARY} ${pct}%, ${SLIDER_TRACK} ${pct}%, ${SLIDER_TRACK} 100%)`;

  return (
    <div className="w-full bg-slate-50 mt-8 md:mt-12 lg:mt-16">
      {/* Range thumb theme color - Tailwind can't style pseudo-elements */}
      <style>{`
        .loan-calc-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 9999px;
          background: ${THEME_PRIMARY};
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(24, 48, 91, 0.3);
        }
        .loan-calc-range::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 9999px;
          background: ${THEME_PRIMARY};
          cursor: pointer;
          border: none;
          box-shadow: 0 1px 3px rgba(24, 48, 91, 0.3);
        }
      `}</style>

      <ScrollReveal variant="fadeUp">
      <section className="w-full flex justify-center overflow-hidden">
        <img
          src="/char3.png"
          alt="Team"
          className="object-contain w-full max-w-[1280px] h-auto min-h-[94px]"
        />
      </section>

      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-slate-800 text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Supporting Your Growth, Every Step of the Way
            </h2>
            <p className="text-slate-600 text-lg md:text-xl mb-6">
              Smart financing solutions for individuals and businesses.
            </p>
            <button
              type="button"
              className="bg-[#18305b] text-white font-semibold rounded-lg px-8 py-4 text-lg hover:bg-[#0d1b2a] hover:-translate-y-0.5 transition-all duration-200"
            >
              Let's Work Together
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="space-y-8">
              <h3 className="text-slate-800 text-2xl md:text-3xl font-bold">
                Calculate Your Monthly Loan
              </h3>

              <ul className="space-y-4">
                {[
                  "Get an instant estimate of your monthly EMI",
                  "Adjust loan amount, interest rate, and tenure easily",
                  "Understand repayment before you apply",
                  "Plan your finances with complete clarity",
                  "Make informed borrowing decisions",
                ].map((text) => (
                  <li key={text} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 mt-0.5 text-[#18305b]">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-slate-600 text-base md:text-lg">{text}</p>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <img src="/char4.png" alt="Loan Calculator" className="w-full max-w-md rounded-xl" />
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8 shadow-lg border border-slate-100">
              <div className="mb-6">
                <label className="block text-slate-600 text-sm font-semibold mb-2">
                  Estimated Amount Required
                </label>
                <input
                  type="text"
                  value={`₹ ${loanAmount.toLocaleString("en-IN")}`}
                  readOnly
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg mb-3 text-slate-800 font-medium focus:border-[#18305b] focus:ring-2 focus:ring-[#18305b]/20 focus:outline-none"
                />
                <input
                  type="range"
                  min="10000"
                  max="4000000"
                  step="10000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="loan-calc-range w-full h-2 rounded-lg appearance-none cursor-pointer bg-transparent"
                  style={{ ...sliderStyle, background: sliderGradient(((loanAmount - 10000) / (4000000 - 10000)) * 100) }}
                />
                <div className="flex justify-between text-slate-500 text-xs mt-1">
                  <span>₹10K</span>
                  <span>₹40L</span>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-slate-600 text-sm font-semibold mb-2">
                  Your Preferred Loan Term (In Months)
                </label>
                <input
                  type="text"
                  value={loanTerm}
                  readOnly
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg mb-3 text-slate-800 font-medium focus:border-[#18305b] focus:ring-2 focus:ring-[#18305b]/20 focus:outline-none"
                />
                <input
                  type="range"
                  min="6"
                  max="60"
                  step="6"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(Number(e.target.value))}
                  className="loan-calc-range w-full h-2 rounded-lg appearance-none cursor-pointer bg-transparent"
                  style={{ ...sliderStyle, background: sliderGradient(((loanTerm - 6) / (60 - 6)) * 100) }}
                />
                <div className="flex justify-between text-slate-500 text-xs mt-1">
                  <span>6 Months</span>
                  <span>60 Months</span>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-slate-600 text-sm font-semibold mb-2">
                  Interest Rate
                </label>
                <input
                  type="text"
                  value={`${interestRate}%`}
                  readOnly
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg mb-3 text-slate-800 font-medium focus:border-[#18305b] focus:ring-2 focus:ring-[#18305b]/20 focus:outline-none"
                />
                <input
                  type="range"
                  min="0.5"
                  max="1"
                  step="0.05"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="loan-calc-range w-full h-2 rounded-lg appearance-none cursor-pointer bg-transparent"
                  style={{ ...sliderStyle, background: sliderGradient(((interestRate - 0.5) / (1 - 0.5)) * 100) }}
                />
                <div className="flex justify-between text-slate-500 text-xs mt-1">
                  <span>0.5%</span>
                  <span>1%</span>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6 mb-6">
                <h4 className="text-slate-800 text-lg font-bold mb-4">Loan Summary</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Loan Amount</span>
                    <span className="text-slate-800 font-semibold">₹ {loanAmount.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tenure</span>
                    <span className="text-slate-800 font-semibold">{loanTerm} Months</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Interest Rate</span>
                    <span className="text-slate-800 font-semibold">{interestRate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">APR</span>
                    <span className="text-slate-800 font-semibold">{apr.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-slate-200">
                    <span className="text-slate-800 text-lg font-bold">Total Payable</span>
                    <span className="text-slate-800 text-lg font-bold">₹ {totalPayable.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="w-full bg-[#18305b] text-white font-semibold py-4 rounded-lg text-lg hover:bg-[#0d1b2a] hover:-translate-y-px transition-all duration-200"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>
    </div>
  );
};

export default LoanCalculator;

